// src/components/operations/WorkflowsTab.jsx
// Workflows tab with DAG visualization
import { useState, useEffect, useCallback } from 'react';
import { useGraphQL } from '../../graphql';
import { useWorkflowNavigation } from '../../context/WorkflowNavigationContext';
import WorkflowCanvas from '../workflows/WorkflowCanvas';
import StartWorkflowModal from '../workflows/StartWorkflowModal';
import ScheduleWorkflowModal from '../workflows/ScheduleWorkflowModal';
import YAMLWorkflowModal from '../workflows/YAMLWorkflowModal';
import ActionFeedback from '../common/ActionFeedback';
import { useActionFeedback } from '../../hooks/useActionFeedback';
import { WORKFLOW_STATUS_STYLES } from '../../constants/workflows';
import {
    PlayIcon,
    ArrowPathIcon,
    StopIcon,
    ChevronRightIcon,
    XMarkIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    ArrowDownTrayIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';

const WorkflowsTab = ({
    selectedRunId,
    onSelectRun,
    onNavigateToJob,
    autoRefresh,
    refreshTrigger,
    onStatsUpdate,
}) => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
    const { setWorkflowContext } = useWorkflowNavigation();

    // State
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [runs, setRuns] = useState([]);
    const [selectedRun, setSelectedRun] = useState(null);
    const [showStartModal, setShowStartModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scheduledWorkflowJobs, setScheduledWorkflowJobs] = useState([]);
    const { feedback, showSuccess, showError, clearFeedback } = useActionFeedback();

    // Load templates on mount
    const loadTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await executeQuery(queries.getWorkflowTemplates());
            setTemplates(data?.workflowTemplates || []);
            if (data?.workflowTemplates?.length > 0 && !selectedTemplate) {
                setSelectedTemplate(data.workflowTemplates[0]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [executeQuery, queries, selectedTemplate]);

    // Load scheduled workflow jobs
    const loadScheduledWorkflowJobs = useCallback(async () => {
        try {
            const data = await executeQuery(queries.getJobs(), { limit: 200, offset: 0, isScheduled: true });
            const workflowJobs = (data.adminJobs || []).filter(
                j => j.isScheduled && (j.type === 'RUN_WORKFLOW' || j.type === 'RUN_COMPANY_WORKFLOWS')
            );
            setScheduledWorkflowJobs(workflowJobs);
        } catch (err) {
            console.error('Failed to load scheduled workflow jobs:', err);
        }
    }, [executeQuery, queries]);

    useEffect(() => {
        loadTemplates();
        loadScheduledWorkflowJobs();
    }, [refreshTrigger]);

    // Load runs when template selected
    const loadRuns = useCallback(async (templateId) => {
        if (!templateId) return;
        try {
            const data = await executeQuery(queries.getWorkflowRuns(templateId, null, 50));
            setRuns(data?.workflowRuns || []);

            // Update stats
            const runningCount = (data?.workflowRuns || []).filter(r => r.status === 'RUNNING').length;
            onStatsUpdate?.(runningCount);
        } catch (err) {
            console.error('Failed to load runs:', err);
        }
    }, [executeQuery, queries, onStatsUpdate]);

    useEffect(() => {
        if (selectedTemplate) {
            loadRuns(selectedTemplate.id);
        }
    }, [selectedTemplate?.id]);

    // Load run detail
    const loadRunDetail = useCallback(async (runId) => {
        try {
            const data = await executeQuery(queries.getWorkflowRun(runId));
            if (data?.workflowRun) {
                setSelectedRun(data.workflowRun);
                onSelectRun?.(runId);
            }
        } catch (err) {
            console.error('Failed to load run detail:', err);
        }
    }, [executeQuery, queries, onSelectRun]);

    // Handle selectedRunId from parent
    useEffect(() => {
        if (selectedRunId && !selectedRun) {
            loadRunDetail(selectedRunId);
        }
    }, [selectedRunId]);

    // Auto-refresh for active runs
    useEffect(() => {
        if (!autoRefresh || !selectedRun) return;
        if (selectedRun.status !== 'RUNNING' && selectedRun.status !== 'PENDING') return;

        const interval = setInterval(() => {
            loadRunDetail(selectedRun.id);
            if (selectedTemplate) {
                loadRuns(selectedTemplate.id);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [autoRefresh, selectedRun?.id, selectedRun?.status, selectedTemplate?.id]);

    const handleStartWorkflow = async (input) => {
        try {
            const result = await executeMutation(mutations.startWorkflow, {
                templateName: selectedTemplate.name,
                input: Object.keys(input).length > 0 ? input : null,
            });

            if (result?.startWorkflow?.success) {
                loadRuns(selectedTemplate.id);
                if (result.startWorkflow.run) {
                    setSelectedRun(result.startWorkflow.run);
                    loadRunDetail(result.startWorkflow.run.id);
                }
            } else if (result?.startWorkflow?.error) {
                alert(result.startWorkflow.error);
            }
        } catch (err) {
            alert('Failed to start workflow: ' + err.message);
        }
    };

    const handleCancelWorkflow = async (runId, force = false) => {
        try {
            const result = await executeMutation(mutations.cancelWorkflow, { runId, force });
            if (result?.cancelWorkflow?.success) {
                if (force) {
                    setSelectedRun(null);
                    if (selectedTemplate) {
                        loadRuns(selectedTemplate.id);
                    }
                } else {
                    loadRunDetail(runId);
                }
            } else if (result?.cancelWorkflow?.error) {
                alert(result.cancelWorkflow.error);
            }
        } catch (err) {
            alert(`Failed to ${force ? 'delete' : 'cancel'} workflow: ` + err.message);
        }
    };

    const handleDeleteWorkflowRun = async (runId) => {
        const run = runs.find(r => r.id === runId) || selectedRun;
        const isRunning = run?.status === 'RUNNING';
        const message = isRunning
            ? 'Force cancel and permanently delete this running workflow?'
            : 'Permanently delete this workflow run?';
        if (!confirm(message)) return;
        await handleCancelWorkflow(runId, true);
    };

    const handleSaveWorkflowYAML = async (yaml) => {
        try {
            const result = await executeMutation(mutations.saveWorkflowYAML, { yaml });
            const data = result?.saveWorkflowYAML;

            if (data?.success) {
                await loadTemplates();
                if (data.template) {
                    const fullTemplate = templates.find(t => t.id === data.template.id) || data.template;
                    setSelectedTemplate(fullTemplate);
                }
                return { success: true };
            } else {
                return { success: false, error: data?.error, parseError: data?.parseError, lineNumber: data?.lineNumber };
            }
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const handleLoadTemplateYAML = async (templateId) => {
        try {
            const data = await executeQuery(queries.getWorkflowTemplateYAML(templateId, null));
            return data?.workflowTemplateYAML || { success: false, error: 'No data returned' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const handleReloadTemplates = async () => {
        try {
            const result = await executeMutation(mutations.reloadWorkflowTemplates, {});
            if (result?.reloadWorkflowTemplates?.success) {
                showSuccess(`Reloaded ${result.reloadWorkflowTemplates.loadedCount} templates from server`);
                await loadTemplates();
            } else {
                showError(result?.reloadWorkflowTemplates?.error || 'Reload failed');
            }
        } catch (err) {
            showError('Failed to reload templates: ' + err.message);
        }
    };

    const handleReconcileSchedule = async () => {
        try {
            const result = await executeMutation(mutations.reconcileSchedule, {});
            const data = result?.reconcileSchedule;
            if (data?.success) {
                const changes = [
                    data.schedulesCreated && `${data.schedulesCreated} schedules created`,
                    data.schedulesUpdated && `${data.schedulesUpdated} schedules updated`,
                    data.schedulesDeleted && `${data.schedulesDeleted} schedules deleted`,
                    data.bindingsCreated && `${data.bindingsCreated} bindings created`,
                    data.bindingsUpdated && `${data.bindingsUpdated} bindings updated`,
                    data.bindingsDeleted && `${data.bindingsDeleted} bindings deleted`,
                ].filter(Boolean);
                const summary = changes.length > 0
                    ? `Schedule reconciled: ${changes.join(', ')}`
                    : 'Schedule reconciled: no changes needed';
                showSuccess(summary);
                await loadScheduledWorkflowJobs();
            } else {
                showError(data?.error || 'Reconcile failed');
            }
        } catch (err) {
            showError('Failed to reconcile schedule: ' + err.message);
        }
    };

    const handleDownloadYAML = async (template) => {
        try {
            const result = await handleLoadTemplateYAML(template.id);
            if (result?.success && result?.yaml) {
                const blob = new Blob([result.yaml], { type: 'text/yaml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${template.name}.yaml`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
        } catch (err) {
            alert('Failed to download: ' + err.message);
        }
    };

    const handleDeleteTemplate = async (template) => {
        if (template.isBuiltIn) {
            alert('Cannot delete built-in templates');
            return;
        }
        if (!confirm(`Delete template "${template.name}"?`)) return;
        try {
            const result = await executeMutation(mutations.deleteWorkflowTemplate, { id: template.id });
            if (result?.deleteWorkflowTemplate?.success) {
                if (selectedTemplate?.id === template.id) {
                    setSelectedTemplate(null);
                }
                await loadTemplates();
            }
        } catch (err) {
            alert('Failed to delete template: ' + err.message);
        }
    };

    // Handle step click - navigate to job with context
    const handleStepClick = (step, state) => {
        if (state?.jobId) {
            setWorkflowContext({
                runId: selectedRun?.id,
                templateId: selectedTemplate?.id,
                templateName: selectedTemplate?.name,
                stepId: step?.id || step?.name,
            });
            onNavigateToJob?.(state.jobId);
        }
    };

    // Get schedules for template
    const getSchedulesForTemplate = useCallback((templateName) => {
        return scheduledWorkflowJobs.filter(job => {
            try {
                const params = typeof job.parameters === 'string' ? JSON.parse(job.parameters) : job.parameters;
                return params?.templateName === templateName;
            } catch { return false; }
        });
    }, [scheduledWorkflowJobs]);

    const toggleScheduledJob = async (job) => {
        try {
            const mutation = job.enabled ? mutations.adminDisableScheduledJob : mutations.adminEnableScheduledJob;
            await executeMutation(mutation, { jobId: job.id });
            loadScheduledWorkflowJobs();
        } catch (err) {
            alert('Failed to toggle schedule: ' + err.message);
        }
    };

    const getCronDescription = (cronSchedule) => {
        if (!cronSchedule) return '';
        const parts = cronSchedule.split(' ');
        if (parts.length < 5) return cronSchedule;
        const [minute, hour, , , dayOfWeek] = parts;
        if (minute.startsWith('*/')) return `Every ${minute.slice(2)}min`;
        if (hour.startsWith('*/')) return `Every ${hour.slice(2)}h`;
        if (dayOfWeek !== '*') {
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            return `${days[parseInt(dayOfWeek)] || dayOfWeek} ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        }
        return `Daily ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    };

    if (loading && templates.length === 0) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-950">
                <div className="text-center">
                    <ArrowPathIcon className="w-8 h-8 text-gray-500 animate-spin mx-auto mb-2" />
                    <span className="text-gray-500 font-mono text-sm">LOADING WORKFLOWS...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-950">
                <div className="text-center">
                    <p className="text-red-600 font-mono text-sm mb-4">ERROR: {error}</p>
                    <button onClick={loadTemplates} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 font-mono text-xs hover:bg-slate-50 hover:border-slate-300 transition-all duration-150">
                        RETRY
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex bg-gray-950">
            {/* Modals */}
            <StartWorkflowModal
                isOpen={showStartModal}
                onClose={() => setShowStartModal(false)}
                template={selectedTemplate}
                onStart={handleStartWorkflow}
            />
            <ScheduleWorkflowModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                template={selectedTemplate}
                onScheduled={loadScheduledWorkflowJobs}
            />
            <YAMLWorkflowModal
                isOpen={showTemplateModal}
                onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
                template={editingTemplate}
                onSave={handleSaveWorkflowYAML}
                onLoadYAML={handleLoadTemplateYAML}
            />

            {/* Left Panel - Template List */}
            <div className="w-64 border-r border-gray-800 bg-gray-900 overflow-y-auto">
                <div className="p-2">
                    <ActionFeedback feedback={feedback} onDismiss={clearFeedback} className="mb-2" />
                    <button
                        onClick={() => { setEditingTemplate(null); setShowTemplateModal(true); }}
                        className="w-full mb-2 px-3 py-2 rounded-lg bg-white text-slate-700 font-mono text-xs hover:bg-slate-50 hover:border-slate-300 flex items-center gap-2 justify-center border border-slate-200 transition-all duration-150"
                    >
                        <PlusIcon className="h-4 w-4" />
                        NEW TEMPLATE
                    </button>
                    <div className="flex gap-1 mb-2">
                        <button
                            onClick={handleReloadTemplates}
                            className="flex-1 px-2 py-1.5 rounded-lg bg-white text-slate-600 font-mono text-xs hover:bg-slate-50 hover:border-slate-300 border border-slate-200 transition-all duration-150"
                            title="Reload built-in templates from server YAML files"
                        >
                            RELOAD
                        </button>
                        <button
                            onClick={handleReconcileSchedule}
                            className="flex-1 px-2 py-1.5 rounded-lg bg-white text-slate-600 font-mono text-xs hover:bg-slate-50 hover:border-slate-300 border border-slate-200 transition-all duration-150"
                            title="Re-apply _schedule.yaml to sync scheduled jobs and event bindings"
                        >
                            RECONCILE
                        </button>
                    </div>

                    {templates.map(template => {
                        const isSelected = selectedTemplate?.id === template.id;
                        const schedules = getSchedulesForTemplate(template.name);

                        return (
                            <div
                                key={template.id}
                                className={`group relative mb-1 rounded-lg transition-all duration-150 ${
                                    isSelected ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-slate-50 border-l-2 border-transparent'
                                }`}
                            >
                                <button
                                    onClick={() => { setSelectedTemplate(template); setSelectedRun(null); }}
                                    className={`w-full text-left px-3 py-2 font-mono text-sm ${
                                        isSelected ? 'text-primary-dark' : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold uppercase text-xs truncate">
                                            {template.name.replace(/-/g, ' ')}
                                        </span>
                                        {template.isBuiltIn && (
                                            <span className="text-xs text-gray-600">BUILT-IN</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {template.steps?.length || 0} steps
                                    </div>
                                    {schedules.map(sj => (
                                        <div key={sj.id} className="flex items-center gap-1 mt-1">
                                            <ClockIcon className="w-3 h-3 text-violet-700" />
                                            <span className="text-xs text-violet-700 truncate">
                                                {getCronDescription(sj.cronSchedule)}
                                            </span>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleScheduledJob(sj); }}
                                                className={`px-1 font-mono text-xs ${
                                                    sj.enabled ? 'text-green-700 hover:text-yellow-700' : 'text-yellow-700 hover:text-green-700'
                                                }`}
                                            >
                                                {sj.enabled ? 'ON' : 'OFF'}
                                            </button>
                                        </div>
                                    ))}
                                </button>
                                {/* Action buttons on hover */}
                                <div className="absolute right-2 top-2 hidden group-hover:flex items-center gap-1">
                                    <button onClick={(e) => { e.stopPropagation(); handleDownloadYAML(template); }} className="p-1 text-slate-500 hover:text-slate-900" title="Download YAML">
                                        <ArrowDownTrayIcon className="h-3 w-3" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setEditingTemplate(template); setShowTemplateModal(true); }} className="p-1 text-slate-500 hover:text-slate-900" title="Edit">
                                        <PencilIcon className="h-3 w-3" />
                                    </button>
                                    {!template.isBuiltIn && (
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template); }} className="p-1 text-slate-500 hover:text-red-600" title="Delete">
                                            <TrashIcon className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Center Panel - DAG Visualization */}
            <div className="flex-1 flex flex-col min-w-0 bg-gray-950">
                {selectedTemplate && (
                    <>
                        {/* Template Header */}
                        <div className="border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                                <h2 className="font-mono text-slate-900 font-bold">{selectedTemplate.name}</h2>
                                <p className="text-xs text-slate-500 mt-1">{selectedTemplate.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowScheduleModal(true)}
                                    className="px-3 py-1.5 rounded-lg bg-violet-600 text-on-accent font-mono text-xs font-bold hover:bg-violet-500 flex items-center gap-1 transition-all duration-150"
                                >
                                    <ClockIcon className="w-4 h-4" />
                                    SCHEDULE
                                </button>
                                <button
                                    onClick={() => setShowStartModal(true)}
                                    className="px-3 py-1.5 rounded-lg bg-green-600 text-on-accent font-mono text-xs font-bold hover:bg-green-500 flex items-center gap-1 transition-all duration-150"
                                >
                                    <PlayIcon className="w-4 h-4" />
                                    START
                                </button>
                            </div>
                        </div>

                        {/* DAG Canvas */}
                        <div className="flex-1 p-4">
                            <WorkflowCanvas
                                template={selectedRun?.template || selectedTemplate}
                                run={selectedRun}
                                onStepClick={handleStepClick}
                                className="h-full"
                            />
                        </div>

                        {/* Recent Runs */}
                        <div className="h-48 border-t border-gray-800 overflow-y-auto">
                            <div className="px-4 py-2 bg-gray-900 border-b border-gray-800 font-mono text-xs text-gray-500 uppercase">
                                Recent Runs
                            </div>
                            {runs.length === 0 ? (
                                <p className="text-gray-500 font-mono text-xs p-4">No runs yet</p>
                            ) : (
                                <table className="w-full">
                                    <tbody>
                                        {runs.map(run => (
                                            <tr
                                                key={run.id}
                                                onClick={() => loadRunDetail(run.id)}
                                                className={`cursor-pointer transition-all duration-150 ${
                                                    selectedRun?.id === run.id ? 'bg-primary/10' : 'hover:bg-slate-50'
                                                }`}
                                            >
                                                <td className="px-4 py-2 font-mono text-xs text-slate-500">
                                                    {run.id.substring(0, 8)}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <span className={`px-2 py-0.5 rounded font-mono text-xs font-bold ${
                                                        WORKFLOW_STATUS_STYLES[run.status]?.bg || 'bg-slate-100'
                                                    } ${WORKFLOW_STATUS_STYLES[run.status]?.text || 'text-slate-700'}`}>
                                                        {run.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 font-mono text-xs text-gray-500">
                                                    {run.startedAt ? new Date(run.startedAt).toLocaleString() : '-'}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Right Panel - Run Details */}
            {selectedRun && (
                <div className="w-96 border-l border-slate-200 bg-white flex flex-col">
                    <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                        <span className="font-mono text-sm text-slate-900 font-bold">RUN DETAILS</span>
                        <button onClick={() => { setSelectedRun(null); onSelectRun?.(null); }} className="text-slate-500 hover:text-slate-900">
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {/* Status */}
                        <div className="flex items-center justify-between">
                            <span className={`px-2 py-0.5 rounded font-mono text-xs font-bold ${
                                WORKFLOW_STATUS_STYLES[selectedRun.status]?.bg || 'bg-slate-100'
                            } ${WORKFLOW_STATUS_STYLES[selectedRun.status]?.text || 'text-slate-700'}`}>
                                {selectedRun.status}
                            </span>
                            <div className="flex items-center gap-2">
                                {selectedRun.status === 'RUNNING' && (
                                    <button
                                        onClick={() => handleCancelWorkflow(selectedRun.id)}
                                        className="px-2 py-1 rounded-lg bg-yellow-500 text-on-accent font-mono text-xs font-bold hover:bg-yellow-400 transition-all duration-150"
                                    >
                                        <StopIcon className="w-3 h-3 inline mr-1" />
                                        CANCEL
                                    </button>
                                )}
                                <button
                                    onClick={() => handleDeleteWorkflowRun(selectedRun.id)}
                                    className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 font-mono text-xs font-bold hover:bg-red-600 hover:border-red-600 hover:text-on-accent transition-all duration-150"
                                >
                                    <TrashIcon className="w-3 h-3 inline mr-1" />
                                    DELETE
                                </button>
                            </div>
                        </div>

                        {/* Run Info */}
                        <div className="space-y-2">
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Run ID</div>
                                <div className="font-mono text-xs text-slate-900 break-all">{selectedRun.id}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <div className="font-mono text-xs text-slate-400 uppercase">Started</div>
                                    <div className="font-mono text-xs text-slate-900">
                                        {selectedRun.startedAt ? new Date(selectedRun.startedAt).toLocaleString() : '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-mono text-xs text-slate-400 uppercase">Completed</div>
                                    <div className="font-mono text-xs text-slate-900">
                                        {selectedRun.completedAt ? new Date(selectedRun.completedAt).toLocaleString() : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Input */}
                        {selectedRun.input && Object.keys(selectedRun.input).length > 0 && (
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase mb-2">Input</div>
                                <pre className="surface-card p-2 font-mono text-xs text-slate-700 overflow-x-auto max-h-32">
                                    {JSON.stringify(selectedRun.input, null, 2)}
                                </pre>
                            </div>
                        )}

                        {/* Step States */}
                        <div>
                            <div className="font-mono text-xs text-slate-400 uppercase mb-2">Steps</div>
                            <div className="space-y-2">
                                {selectedRun.stepStates?.map(ss => (
                                    <div key={ss.stepId} className="surface-card p-2">
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-xs text-slate-900 font-bold">{ss.stepId}</span>
                                            <span className={`px-1.5 py-0.5 rounded font-mono text-xs ${
                                                WORKFLOW_STATUS_STYLES[ss.status]?.bg || 'bg-slate-100'
                                            } ${WORKFLOW_STATUS_STYLES[ss.status]?.text || 'text-slate-700'}`}>
                                                {ss.status}
                                            </span>
                                        </div>
                                        {ss.jobId && (
                                            <button
                                                onClick={() => handleStepClick({ id: ss.stepId }, { jobId: ss.jobId })}
                                                className="mt-1 font-mono text-xs text-blue-600 hover:text-blue-700"
                                            >
                                                View Job: {ss.jobId.substring(0, 8)}...
                                            </button>
                                        )}
                                        {ss.error && (
                                            <div className="mt-1 font-mono text-xs text-red-700 truncate" title={ss.error}>
                                                {ss.error}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Error */}
                        {selectedRun.error && (
                            <div>
                                <div className="font-mono text-xs text-red-600 uppercase mb-2">Error</div>
                                <pre className="bg-red-50 border border-red-200 rounded-lg p-2 font-mono text-xs text-red-700 overflow-x-auto">
                                    {selectedRun.error}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowsTab;
